import { useState } from 'react';

import {
  CLAUSE_MINING_DRY_RUN_REASON_MAX_LENGTH,
  type ClauseMiningDryRunFilter,
  type ClauseMiningDryRunStage,
} from '@clm/shared-types';

import { requestClauseMiningDryRun, runClauseMiningDryRunNow } from '../../api/clause-mining-dry-runs';
import { mayAttempt } from '../../auth/permissions';
import { useIdentity } from '../../auth/useIdentity';
import { PIPELINE_FORBIDDEN_COPY } from '../pipeline-status-chips';
import { APPROVE_ROLES, REQUEST_ROLES, dryRunProblemHead, toDryRunProblem, type DryRunProblem } from './shared';

/**
 * Request a mine-clauses or match-clauses **dry run** — CLM-103 (mine-clauses
 * first slice; match-clauses added in the completion slice, same form and
 * request shape, one more `stage` option).
 *
 * Deliberately the only thing this form can ask for. There is no live/dry-run
 * toggle here, on purpose: the request body this form builds
 * (`ClauseMiningDryRunFilter`) has no field that could carry one, and
 * `RequestClauseMiningDryRunRequestSchema` is `.strict()`, so a live run is
 * not just discouraged by this UI, it is not expressible through it. A live
 * (money-spending) run stays on the `mine-clauses` or `match-clauses` GitHub
 * Actions workflow — see the section's own note.
 *
 * Shown to everyone. **Request** dims for an identity without the contributor
 * role; **Run now** is hidden for an identity that is known not to be an
 * admin (`mayAttempt` — usability only; the API is the gate). Run now creates
 * *and* approves in one action, so the worker picks the dry run up within its
 * poll interval — safe to self-serve even for a contributor request, since a
 * dry run spends nothing, but the approval step is kept for parity with the
 * Pipeline page's Runs section (see the auth action's own note).
 */
export function RequestDryRunForm({ onDone }: { readonly onDone: () => void }) {
  const { identity } = useIdentity();
  const mayRequest = mayAttempt(identity, REQUEST_ROLES);
  const mayRunNow = mayAttempt(identity, APPROVE_ROLES);

  const [stage, setStage] = useState<ClauseMiningDryRunStage>('segment');
  const [limit, setLimit] = useState('20');
  const [contractKey, setContractKey] = useState('');
  const [paragraphLimit, setParagraphLimit] = useState('');
  const [top, setTop] = useState('50');
  const [matchLimit, setMatchLimit] = useState('');
  const [matchContractKey, setMatchContractKey] = useState('');
  const [rematchUnreviewed, setRematchUnreviewed] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<DryRunProblem | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const filter = (): ClauseMiningDryRunFilter => {
    if (stage === 'segment') {
      const parsedLimit = Number.parseInt(limit, 10);
      return {
        stage: 'segment',
        limit: Number.isFinite(parsedLimit) ? parsedLimit : 20,
        ...(contractKey.trim() === '' ? {} : { contract_key: contractKey.trim() }),
      };
    }
    if (stage === 'cluster') {
      const parsed = Number.parseInt(paragraphLimit, 10);
      return {
        stage: 'cluster',
        ...(paragraphLimit.trim() === '' || !Number.isFinite(parsed) ? {} : { paragraph_limit: parsed }),
      };
    }
    if (stage === 'describe') {
      const parsedTop = Number.parseInt(top, 10);
      return { stage: 'describe', top: Number.isFinite(parsedTop) ? parsedTop : 50 };
    }
    const parsedMatchLimit = Number.parseInt(matchLimit, 10);
    return {
      stage: 'match',
      ...(matchLimit.trim() === '' || !Number.isFinite(parsedMatchLimit) ? {} : { limit: parsedMatchLimit }),
      ...(matchContractKey.trim() === '' ? {} : { contract_key: matchContractKey.trim() }),
      ...(rematchUnreviewed ? { rematch_unreviewed: true } : {}),
    };
  };

  const submit = async (mode: 'request' | 'now') => {
    setBusy(true);
    setProblem(null);
    setDone(null);
    try {
      const body = { filter: filter(), reason: reason.trim() };
      const response = mode === 'now' ? await runClauseMiningDryRunNow(body) : await requestClauseMiningDryRun(body);
      setDone(
        mode === 'now'
          ? `Dry run ${response.run.clause_mining_dry_run_id} approved — the worker starts it shortly`
          : `Dry run ${response.run.clause_mining_dry_run_id} requested — awaiting an admin`,
      );
      setReason('');
      onDone();
    } catch (error) {
      setProblem(toDryRunProblem(error));
    } finally {
      setBusy(false);
    }
  };

  const reasonMissing = reason.trim() === '';

  return (
    <div className="vform" role="group" aria-label="Request a mine-clauses dry run">
      <div className="field">
        <label htmlFor="mining-stage">Stage</label>
        <select
          id="mining-stage"
          value={stage}
          disabled={busy}
          onChange={(event) => setStage(event.target.value as ClauseMiningDryRunStage)}
        >
          <option value="segment">segment</option>
          <option value="cluster">cluster</option>
          <option value="describe">describe</option>
          <option value="match">match</option>
        </select>
      </div>
      {stage === 'segment' ? (
        <>
          <div className="field">
            <label htmlFor="mining-limit">Contracts to segment</label>
            <input
              id="mining-limit"
              type="number"
              min={1}
              max={500}
              value={limit}
              disabled={busy}
              onChange={(event) => setLimit(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="mining-contract-key">Contract key (optional — narrows to one)</label>
            <input
              id="mining-contract-key"
              type="text"
              value={contractKey}
              disabled={busy}
              placeholder="e.g. AGM-0001 or AGM-SOW-0001"
              onChange={(event) => setContractKey(event.target.value)}
            />
          </div>
        </>
      ) : null}
      {stage === 'cluster' ? (
        <div className="field">
          <label htmlFor="mining-paragraph-limit">Paragraphs to read (blank = the whole corpus)</label>
          <input
            id="mining-paragraph-limit"
            type="number"
            min={1}
            value={paragraphLimit}
            disabled={busy}
            onChange={(event) => setParagraphLimit(event.target.value)}
          />
        </div>
      ) : null}
      {stage === 'describe' ? (
        <div className="field">
          <label htmlFor="mining-top">Clusters to describe</label>
          <input
            id="mining-top"
            type="number"
            min={1}
            max={500}
            value={top}
            disabled={busy}
            onChange={(event) => setTop(event.target.value)}
          />
        </div>
      ) : null}
      {stage === 'match' ? (
        <>
          <div className="field">
            <label htmlFor="matching-limit">Contracts to match (blank = MatchConfig default)</label>
            <input
              id="matching-limit"
              type="number"
              min={1}
              max={500}
              value={matchLimit}
              disabled={busy}
              onChange={(event) => setMatchLimit(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="matching-contract-key">Contract key (optional — narrows to one)</label>
            <input
              id="matching-contract-key"
              type="text"
              value={matchContractKey}
              disabled={busy}
              placeholder="e.g. AGM-0001 or AGM-SOW-0001"
              onChange={(event) => setMatchContractKey(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="matching-rematch-unreviewed">
              <input
                id="matching-rematch-unreviewed"
                type="checkbox"
                checked={rematchUnreviewed}
                disabled={busy}
                onChange={(event) => setRematchUnreviewed(event.target.checked)}
              />{' '}
              Re-evaluate every unreviewed row regardless of band
            </label>
          </div>
        </>
      ) : null}
      <div className="field">
        <label htmlFor="mining-reason">Reason (required)</label>
        <textarea
          id="mining-reason"
          value={reason}
          disabled={busy}
          maxLength={CLAUSE_MINING_DRY_RUN_REASON_MAX_LENGTH}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      <p className="mut">
        Dry run only — this reads and reports, writes nothing and calls no model, for every stage. A live
        (writing, spending) run stays on the <code>mine-clauses</code> or <code>match-clauses</code> GitHub
        Actions workflow.
      </p>
      <div className="vacts">
        <button
          type="button"
          className="btn pri"
          disabled={!mayRequest || busy || reasonMissing}
          title={mayRequest ? undefined : 'Contributor role required'}
          onClick={() => void submit('request')}
        >
          Request dry run
        </button>
        <button
          type="button"
          className="btn"
          hidden={!mayRunNow}
          disabled={busy || reasonMissing}
          title="Admin — creates and approves the dry run in one action"
          onClick={() => void submit('now')}
        >
          Run now
        </button>
      </div>
      {done === null ? null : (
        <p role="status" className="mut">
          {done}
        </p>
      )}
      {problem === null ? null : (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>{dryRunProblemHead(problem, PIPELINE_FORBIDDEN_COPY)}</strong>
          </p>
          {problem.field_errors.map((entry) => (
            <p key={entry.path} className="mut">
              {entry.message}
            </p>
          ))}
          {problem.correlation_id === null ? null : (
            <p className="mut">
              Correlation <code>{problem.correlation_id}</code>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
